// Full-colour illustrations for "Industries We Serve". Drawn as one set on a
// 48×48 grid: each uses its industry colour as a gradient body, a deeper shade
// for structure and shadow, and white/pale highlights. Inline SVG, so no
// image downloads.

import { shade } from '../utils/color';

// p = palette: light/base/dark shades of the industry colour, plus pale and
// white; g = url() of this icon's body gradient.
const DRAWINGS = {
  retail: (p, g) => (
    <>
      <path d="M17.5 19v-4.5a6.5 6.5 0 0113 0V19" fill="none" stroke={p.dark} strokeWidth="2.6" strokeLinecap="round" />
      <path d="M10.5 17h27l-1.7 21.3a3 3 0 01-3 2.7H15.2a3 3 0 01-3-2.7z" fill={g} />
      <path d="M10.5 17h27l-.3 3.6H10.8z" fill={p.dark} opacity=".35" />
      <path d="M18.5 29.5l4 4 7.5-7.5" fill="none" stroke={p.white} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="17.5" cy="19" r="1.6" fill={p.dark} />
      <circle cx="30.5" cy="19" r="1.6" fill={p.dark} />
    </>
  ),
  finance: (p, g) => (
    <>
      <ellipse cx="19" cy="36" rx="11" ry="4" fill={p.dark} />
      <rect x="8" y="31" width="22" height="5" fill={p.dark} />
      <ellipse cx="19" cy="31" rx="11" ry="4" fill={g} />
      <rect x="8" y="25" width="22" height="6" fill={p.dark} />
      <ellipse cx="19" cy="25" rx="11" ry="4" fill={g} />
      <rect x="8" y="19" width="22" height="6" fill={p.dark} />
      <ellipse cx="19" cy="19" rx="11" ry="4" fill={p.light} />
      <ellipse cx="19" cy="19" rx="6" ry="2" fill="none" stroke={p.white} strokeWidth="1.4" opacity=".8" />
      <circle cx="35" cy="15" r="8" fill={p.dark} />
      <path d="M35 19.5v-9M31 14.5l4-4 4 4" fill="none" stroke={p.white} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  manufacturing: (p, g) => (
    <>
      <circle cx="37" cy="7.5" r="2.4" fill={p.light} />
      <circle cx="41" cy="5" r="1.7" fill={p.light} />
      <rect x="32" y="10" width="7" height="30" rx="1" fill={p.dark} />
      <path d="M6 40V24l8.5 5v-5l8.5 5v-5l9 5v11z" fill={g} />
      <rect x="10" y="32" width="4" height="4" rx=".8" fill={p.white} />
      <rect x="18" y="32" width="4" height="4" rx=".8" fill={p.white} />
      <rect x="26" y="32" width="4" height="4" rx=".8" fill={p.white} />
      <rect x="4" y="39.5" width="40" height="2.5" rx="1.25" fill={p.dark} />
    </>
  ),
  healthcare: (p, g) => (
    <>
      <path d="M24 41S7 31.5 7 19.5A9 9 0 0124 14.9a9 9 0 0117 4.6C41 31.5 24 41 24 41z" fill={g} />
      <ellipse cx="15" cy="17" rx="3.4" ry="2.2" transform="rotate(-35 15 17)" fill={p.white} opacity=".45" />
      <path d="M11 26h7l2.6-5 4 10 2.6-5H37" fill="none" stroke={p.white} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  realEstate: (p, g) => (
    <>
      <rect x="31" y="9" width="4.5" height="9" rx=".8" fill={p.dark} />
      <path d="M11 21h26v18a2 2 0 01-2 2H13a2 2 0 01-2-2z" fill={g} />
      <path d="M5.5 23.5L24 8.5l18.5 15-2.4 2.9L24 13.3 7.9 26.4z" fill={p.dark} />
      <rect x="20" y="29" width="8" height="12" rx="1.2" fill={p.white} />
      <circle cx="26" cy="35.5" r=".9" fill={p.dark} />
      <rect x="14" y="26" width="4.5" height="4.5" rx=".8" fill={p.pale} />
      <rect x="29.5" y="26" width="4.5" height="4.5" rx=".8" fill={p.pale} />
    </>
  ),
  automotive: (p, g) => (
    <>
      <path d="M4 32.5v-5.2a2 2 0 011.5-1.9l5.5-1.4 5.3-6.4a3 3 0 012.3-1.1h11.6a3 3 0 012.3 1.1l5.3 6.4 5.4 1.4a2 2 0 011.5 1.9v5.2a1.5 1.5 0 01-1.5 1.5h-37A1.5 1.5 0 014 32.5z" fill={g} />
      <path d="M16.5 24l3.6-4.6h3.4V24zM26.5 24v-4.6h3.3l3.8 4.6z" fill={p.pale} />
      <rect x="40" y="27.5" width="3.5" height="2" rx="1" fill={p.white} />
      <circle cx="14" cy="34" r="5" fill={p.dark} />
      <circle cx="14" cy="34" r="2" fill={p.white} />
      <circle cx="34" cy="34" r="5" fill={p.dark} />
      <circle cx="34" cy="34" r="2" fill={p.white} />
    </>
  ),
  logistics: (p, g) => (
    <>
      <rect x="3" y="12" width="26" height="21" rx="2" fill={g} />
      <path d="M8 18h16M8 22.5h11" stroke={p.white} strokeWidth="2" strokeLinecap="round" opacity=".7" />
      <path d="M29 18h8.3a2 2 0 011.6.8l5.3 7a2 2 0 01.4 1.2V31a2 2 0 01-2 2H29z" fill={p.dark} />
      <path d="M32 21h5l3.4 4.6H32z" fill={p.pale} />
      <circle cx="11" cy="34.5" r="4.5" fill={p.dark} />
      <circle cx="11" cy="34.5" r="1.8" fill={p.white} />
      <circle cx="36" cy="34.5" r="4.5" fill={p.dark} />
      <circle cx="36" cy="34.5" r="1.8" fill={p.white} />
    </>
  ),
  education: (p, g) => (
    <>
      <path d="M12 22v9c0 3.3 5.4 6 12 6s12-2.7 12-6v-9z" fill={p.dark} />
      <path d="M24 9.5L3.5 19 24 28.5 44.5 19z" fill={g} />
      <path d="M24 12.5L10 19l14 6.5" fill="none" stroke={p.white} strokeWidth="1.2" opacity=".45" strokeLinejoin="round" />
      <path d="M40.5 21v10" stroke={p.base} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M38.5 31h4l1 5h-6z" fill={p.base} />
    </>
  ),
  hospitality: (p, g) => (
    <>
      <rect x="21" y="10.5" width="6" height="2.6" rx="1.3" fill={p.dark} />
      <rect x="22.8" y="12.5" width="2.4" height="4" fill={p.dark} />
      <path d="M7.5 34a16.5 16.5 0 0133 0z" fill={g} />
      <path d="M14 27.5a11 11 0 016.5-6.2" fill="none" stroke={p.white} strokeWidth="2.4" strokeLinecap="round" opacity=".75" />
      <rect x="4" y="34" width="40" height="4.5" rx="2.25" fill={p.dark} />
      <path d="M40 8.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9z" fill={p.light} />
    </>
  ),
  d2c: (p, g) => (
    <>
      <path d="M24 7.5l17 8-17 8-17-8z" fill={p.light} />
      <path d="M7 15.5l17 8V41L7 33z" fill={g} />
      <path d="M41 15.5l-17 8V41l17-8z" fill={p.dark} />
      <path d="M15.5 11.5l17 8v6.8l-3.8 1.8v-6.8l-17-8z" fill={p.pale} />
      <path d="M11 29.5l5 2.4" stroke={p.white} strokeWidth="1.8" strokeLinecap="round" opacity=".8" />
    </>
  ),
};

export default function IndustryIcon({ name, color, size = 54 }) {
  const draw = DRAWINGS[name];
  if (!draw) return null;
  const p = {
    light: shade(color, 0.35),
    base: color,
    dark: shade(color, -0.3),
    pale: shade(color, 0.72),
    white: '#ffffff',
  };
  const gradId = `industry-grad-${name}`;
  return (
    <svg width={size} height={size} viewBox="3 3 42 42" aria-hidden="true">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={p.light} />
          <stop offset="1" stopColor={p.base} />
        </linearGradient>
      </defs>
      {draw(p, `url(#${gradId})`)}
    </svg>
  );
}
